import { useState, useEffect, useRef, useCallback } from 'react';
import { useCommunicationService } from 'src/app/services/communication';
import { AppMessage, MessageType } from 'src/app/utils/in-app-comunication/types';

type UseTimerProps = {
  timerId: string;
  finalDuration: number;
  autoStart?: boolean;
};

const useTimer = ({ timerId, finalDuration, autoStart = false }: UseTimerProps) => {
  const comms = useCommunicationService();

  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(autoStart);

  // Refs for internal state management (don't trigger re-renders)
  const intervalRef = useRef<number | null>(null); // Store setInterval ID for cleanup
  const isSyncingRef = useRef(false); // Prevent parallel sync messages
  const isProcessingMessageRef = useRef(false); // Prevent duplicate message processing
  const lastSyncTimeRef = useRef<number>(0); // Track last message timestamp
  const isPausedGloballyRef = useRef(false); // Track if timer is paused globally

  const clearTimerInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (intervalRef.current === null) {
      intervalRef.current = window.setInterval(() => {
        setElapsedTime((prev) => {
          if (prev >= finalDuration) {
            clearTimerInterval();
            return finalDuration;
          }
          return prev + 1;
        });
      }, 1000);
    }
  }, [clearTimerInterval, finalDuration]);

  const reset = useCallback(() => {
    clearTimerInterval();
    setElapsedTime(0);
    setIsRunning(false);
  }, [clearTimerInterval]);

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.SLIDESHOW_CHANGE) {
        reset();
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [reset, comms]);

  const play = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
      isPausedGloballyRef.current = false;
      comms.sendMessage({
        type: MessageType.TIMER_PLAY,
        timerId,
        timestamp: Date.now(),
      });
    }
  }, [isRunning, comms, timerId]);

  const pause = useCallback(() => {
    if (isRunning) {
      setIsRunning(false);
      isPausedGloballyRef.current = true;
      clearTimerInterval();
      comms.sendMessage({
        type: MessageType.TIMER_PAUSE,
        timerId,
        elapsedTime,
        timestamp: Date.now(),
      });
    }
  }, [isRunning, comms, timerId, elapsedTime, clearTimerInterval]);

  const restart = useCallback(() => {
    setElapsedTime(0);
    isPausedGloballyRef.current = false;
    comms.sendMessage({
      type: MessageType.TIMER_RESTART,
      timerId,
      elapsedTime: 0,
      timestamp: Date.now(),
    });
  }, [comms, timerId]);

  const syncState = useCallback(() => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    comms.sendMessage({
      type: MessageType.TIMER_SYNC_STATE,
      timerId,
      elapsedTime,
      isRunning,
      timestamp: Date.now(),
    });

    setTimeout(() => {
      isSyncingRef.current = false;
    }, 100);
  }, [timerId, elapsedTime, isRunning, comms]);

  useEffect(() => {
    const handler = (msg: AppMessage) => {
      if (msg.timerId !== timerId) return;

      if (msg.type === MessageType.TIMER_SYNC_STATE && isProcessingMessageRef.current) {
        return;
      }

      const isControlMessage =
        msg.type === MessageType.TIMER_PLAY ||
        msg.type === MessageType.TIMER_PAUSE ||
        msg.type === MessageType.TIMER_RESTART;

      if (!isControlMessage && isProcessingMessageRef.current) {
        return;
      }

      if (!isControlMessage) {
        isProcessingMessageRef.current = true;
      }

      if (msg.timestamp !== undefined) {
        lastSyncTimeRef.current = msg.timestamp;
      }

      try {
        switch (msg.type) {
          case MessageType.TIMER_PLAY:
            setIsRunning(true);
            isPausedGloballyRef.current = false;
            break;
          case MessageType.TIMER_PAUSE:
            setIsRunning(false);
            isPausedGloballyRef.current = true;
            clearTimerInterval();
            if (msg.elapsedTime !== undefined) {
              setElapsedTime(msg.elapsedTime);
            }
            break;
          case MessageType.TIMER_RESTART:
            setIsRunning(false);
            isPausedGloballyRef.current = false;
            setElapsedTime(0);
            break;
          case MessageType.TIMER_SYNC_STATE:
            // Only sync if not globally paused and both instances have same running state
            if (
              !isPausedGloballyRef.current &&
              msg.elapsedTime !== undefined &&
              msg.isRunning !== undefined &&
              msg.isRunning === isRunning
            ) {
              setElapsedTime(msg.elapsedTime);
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error processing timer message:', error);
      } finally {
        if (!isControlMessage) {
          setTimeout(() => {
            isProcessingMessageRef.current = false;
          }, 50);
        }
      }
    };

    const unsubscribe = comms.onMessage(handler);

    if (!(window as any)._TEST_ENV_) {
      setTimeout(() => {
        syncState();
      }, 100);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      clearTimerInterval();
    };
  }, [timerId, comms, clearTimerInterval, syncState]);

  useEffect(() => {
    setElapsedTime(0);
    if (autoStart) {
      setIsRunning(true);
    } else {
      setIsRunning(false);
    }
  }, [finalDuration, autoStart]);

  useEffect(() => {
    if (isRunning && elapsedTime < finalDuration && !isPausedGloballyRef.current) {
      startTimer();
    } else {
      clearTimerInterval();
    }

    return () => clearTimerInterval();
  }, [isRunning, elapsedTime, startTimer, clearTimerInterval, finalDuration]);

  useEffect(() => {
    return () => {
      isProcessingMessageRef.current = false;
      isSyncingRef.current = false;
      lastSyncTimeRef.current = 0;
      isPausedGloballyRef.current = false;
    };
  }, []);

  return {
    elapsedTime,
    isRunning,
    play,
    pause,
    restart,
    reset,
  };
};

export default useTimer;
